import Component from '@glimmer/component';
import { format } from 'date-fns/format';
import pluralize from 'ember-inflector/lib/helpers/pluralize';
import type { VisitorProfileStats } from 'garaje/pods/visitors/profiles/show/route';
import { DATE_FNS_MM_DD_YYYY, DATE_FNS_12_HOUR_TIME } from 'garaje/utils/date-fns-tz-utilities';

interface ProfileStatsSignature {
  Args: {
    stats: VisitorProfileStats;
  };
}

export default class ProfileStatsComponent extends Component<ProfileStatsSignature> {
  get nextUpcomingVisitDateTime(): string {
    if (!this.args.stats.nextUpcomingVisitDateTime) {
      return 'N/A';
    }
    const date = new Date(this.args.stats.nextUpcomingVisitDateTime);
    return format(date, `${DATE_FNS_MM_DD_YYYY} 'at' ${DATE_FNS_12_HOUR_TIME}`);
  }

  get formattedFirstVisitDate(): string {
    if (!this.args.stats.firstVisitDate) {
      return 'N/A';
    }
    const date = new Date(this.args.stats.firstVisitDate);
    return format(date, DATE_FNS_MM_DD_YYYY);
  }

  get formattedLastVisitDate(): string {
    if (!this.args.stats.lastVisitDate) {
      return 'N/A';
    }
    const date = new Date(this.args.stats.lastVisitDate);
    return format(date, DATE_FNS_MM_DD_YYYY);
  }

  get upcomingVisitsCount(): number {
    const {
      upcomingVisitsCount: { recurringCount, nonRecurringCount },
    } = this.args.stats;
    return recurringCount + nonRecurringCount;
  }

  get upcomingVisitsTooltip(): string {
    const {
      upcomingVisitsCount: { recurringCount, nonRecurringCount },
    } = this.args.stats;
    return `${recurringCount} recurring, ${nonRecurringCount} non-recurring`;
  }

  <template>
    <div class="flex flex-row space-x-10" data-test-profile-stats>
      <div class="flex flex-col space-y-2" data-test-total-visits>
        <h3 class="text-sm font-medium text-carbon-50">Total visits</h3>
        <span>{{pluralize @stats.previousSignInsCount "time"}}</span>
      </div>
      <div class="flex flex-col space-y-2" data-test-upcoming-visits>
        <h3 class="text-sm font-medium text-carbon-50">Upcoming visits</h3>
        <div>
          {{! TODO: uncomment tooltip when API breaks down recurring and non-recurring visits }}
          {{!-- <EmberTooltip @side="top-start" @text={{this.upcomingVisitsTooltip}} @tooltipClass="profile-stats-tooltip" /> --}}
          <span>{{pluralize this.upcomingVisitsCount "upcoming visit"}}</span>
        </div>
      </div>
      <div class="flex flex-col space-y-2" data-test-next-upcoming-visit>
        <h3 class="text-sm font-medium text-carbon-50">Next upcoming visit</h3>
        <span>{{this.nextUpcomingVisitDateTime}}</span>
      </div>
      <div class="flex flex-col space-y-2" data-test-first-visited>
        <h3 class="text-sm font-medium text-carbon-50">First visited</h3>
        <span>{{this.formattedFirstVisitDate}}</span>
      </div>
      <div class="flex flex-col space-y-2" data-test-last-visited>
        <h3 class="text-sm font-medium text-carbon-50">Last visited</h3>
        <span>{{this.formattedLastVisitDate}}</span>
      </div>
    </div>
  </template>
}
